/**
 * Utility functions for handling offline/online state
 */

// Event listeners for connection events
let onlineListeners: Array<(online: boolean) => void> = [];

// Current connection status
let isOnline = navigator.onLine;

// Set up event listeners for online/offline events
window.addEventListener('online', () => {
  isOnline = true;
  notifyListeners();
});

window.addEventListener('offline', () => {
  isOnline = false;
  notifyListeners();
});

/**
 * Notify all listeners about connection state changes
 */
function notifyListeners() {
  onlineListeners.forEach(listener => listener(isOnline));
}

/**
 * Check if the application is currently online
 * @returns Current online status
 */
export const checkOnlineStatus = (): boolean => {
  return isOnline;
};

/**
 * Add a listener for online/offline events
 * @param listener Function to call when online status changes
 * @returns Function to remove the listener
 */
export const addOnlineStatusListener = (listener: (online: boolean) => void): (() => void) => {
  onlineListeners.push(listener);
  // Call listener with current status immediately
  listener(isOnline);

  // Return function to remove this listener
  return () => {
    onlineListeners = onlineListeners.filter(l => l !== listener);
  };
};

/**
 * Determine if we should use offline mode
 * This can be extended to check user preferences, etc.
 * @returns True if we should use offline mode
 */
export const shouldUseOfflineMode = (): boolean => {
  // Check if offline, or user has chosen to work offline
  return !isOnline || localStorage.getItem('preferOfflineMode') === 'true';
};

/**
 * Set user preference for offline mode
 * @param preferOffline Whether user prefers offline mode
 */
export const setOfflineModePreference = (preferOffline: boolean): void => {
  localStorage.setItem('preferOfflineMode', preferOffline ? 'true' : 'false');
};

const offlineHelper = {
  checkOnlineStatus,
  addOnlineStatusListener,
  shouldUseOfflineMode,
  setOfflineModePreference
};

export default offlineHelper; 